import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Container,
  Paper,
  TextField,
  Button,
  Typography,
  Box,
  Alert,
  InputAdornment,
  IconButton,
  CircularProgress
} from '@mui/material';
import {
  Visibility,
  VisibilityOff,
  Email,
  Lock
} from '@mui/icons-material';
import MainService from '../services/MainService';

const Login = ({ onLogin }) => {
  const [formData, setFormData] = useState({
    UserName: '',
    Password: ''
  });
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    // Validation
    if (!formData.UserName || !formData.Password) {
      setError('Please fill in all fields');
      setLoading(false);
      return;
    }

    try {
      const response = await MainService.Login({
        UserName: formData.UserName,
        Password: formData.Password
      });

             if (response && response.Role === "Customer") {
         // Store user data in localStorage
         localStorage.setItem('user', JSON.stringify(response));
         localStorage.setItem('isAuthenticated', 'true');
         
         // Store token if available
         if (response.token) {
           localStorage.setItem('token', response.token);
         } else if (response.Token) {
           localStorage.setItem('token', response.Token);
         }
         
         // Call onLogin with user data
         onLogin(response);
       } else {
         setError('Invalid credentials or insufficient permissions');
       }
    } catch (error) {
      console.error('Login error:', error);
      setError('Login failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container 
      maxWidth={false}
      disableGutters
      sx={{ 
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: `linear-gradient(rgba(0, 0, 0, 0.4), rgba(0, 0, 0, 0.4)), url('https://images.unsplash.com/photo-1523741543316-beb7fc7023d8?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=2070&q=80')`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat',
        backgroundAttachment: 'fixed',
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        m: 0,
        p: 0
      }}
    >
      <Paper 
        elevation={8} 
        sx={{ 
          p: 4, 
          borderRadius: 3,
          background: 'rgba(255, 255, 255, 0.95)',
          backdropFilter: 'blur(10px)',
          border: '1px solid rgba(255, 255, 255, 0.2)',
          maxWidth: '400px',
          width: '100%',
          mx: 2,
          position: 'relative',
          zIndex: 1
        }}
      >
        <Box sx={{ textAlign: 'center', mb: 4 }}>
          <Box sx={{ mb: 2 }}>
            <img 
              src="/logo.png" 
              alt="UVANDUS Logo" 
              style={{ 
                height: '60px', 
                width: 'auto',
                maxWidth: '200px'
              }} 
            />
          </Box>
          <Typography variant="h3" sx={{ fontWeight: 'bold', mb: 1, color: '#2E7D32' }}>
            UVANDUS
          </Typography>
          <Typography variant="h6" sx={{ color: '#4CAF50', fontWeight: 500 }}>
            Fresh Delivery Portal
          </Typography>
        </Box>

        <Typography variant="h5" sx={{ textAlign: 'center', mb: 3, fontWeight: 600, color: '#2E7D32' }}>
          Welcome Back
        </Typography>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <Box component="form" onSubmit={handleSubmit}>
                     <TextField
             fullWidth
             label="Mobile No"
             name="UserName"
             type="text"
             value={formData.UserName}
             onChange={handleChange}
             margin="normal"
             variant="outlined"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Email sx={{ color: '#2E7D32' }} />
                </InputAdornment>
              ),
            }}
            sx={{
              '& .MuiOutlinedInput-root': {
                '& fieldset': {
                  borderColor: '#2E7D32',
                },
                '&:hover fieldset': {
                  borderColor: '#4CAF50',
                },
                '&.Mui-focused fieldset': {
                  borderColor: '#2E7D32',
                },
              },
              '& .MuiInputLabel-root': {
                color: '#2E7D32',
              },
              '& .MuiInputBase-input': {
                color: '#2E7D32',
              },
            }}
          />

          <TextField
            fullWidth
            label="Password"
            name="Password"
            type={showPassword ? 'text' : 'password'}
            value={formData.Password}
            onChange={handleChange}
            margin="normal"
            variant="outlined"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Lock sx={{ color: '#2E7D32' }} />
                </InputAdornment>
              ),
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    onClick={() => setShowPassword(!showPassword)}
                    edge="end"
                    sx={{ color: '#2E7D32' }}
                  >
                    {showPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
            sx={{
              '& .MuiOutlinedInput-root': {
                '& fieldset': {
                  borderColor: '#2E7D32',
                },
                '&:hover fieldset': {
                  borderColor: '#4CAF50',
                },
                '&.Mui-focused fieldset': {
                  borderColor: '#2E7D32',
                },
              },
              '& .MuiInputLabel-root': {
                color: '#2E7D32',
              },
              '& .MuiInputBase-input': {
                color: '#2E7D32',
              },
            }}
          />

          <Button
            type="submit"
            fullWidth
            variant="contained"
            size="large"
            disabled={loading}
            sx={{
              mt: 3,
              mb: 2,
              py: 1.5,
              backgroundColor: '#2E7D32',
              color: 'white',
              fontWeight: 'bold',
              '&:hover': {
                backgroundColor: '#1B5E20',
              },
              '&:disabled': {
                backgroundColor: 'rgba(46, 125, 50, 0.7)',
                color: 'rgba(255, 255, 255, 0.7)',
              },
            }}
          >
            {loading ? (
              <CircularProgress size={24} sx={{ color: 'white' }} />
            ) : (
              'Sign In'
            )}
          </Button>

          <Box sx={{ textAlign: 'center' }}>
            <Typography variant="body2" sx={{ color: '#2E7D32' }}>
              Don't have an account?{' '}
              <Link 
                to="/register" 
                style={{ 
                  color: '#4CAF50', 
                  textDecoration: 'none',
                  fontWeight: 'bold'
                }}
              >
                Sign up here
              </Link>
            </Typography>
          </Box>
        </Box>
      </Paper>
    </Container>
  );
};

export default Login; 